import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Paper,
  Container,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Button,
  Chip,
  Card,
  CardContent,
  Grid,
  IconButton,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Alert,
  CircularProgress,
  Divider
} from '@mui/material';
import {
  History,
  FilterList,
  Download,
  Refresh,
  Visibility,
  Delete,
  TrendingUp,
  Person,
  Business,
  Store,
  AccountBalance,
  Settings
} from '@mui/icons-material';
import { useQuery, useMutation, useQueryClient } from 'react-query';
import api from '../../services/api';

const ActivityLog = () => {
  const queryClient = useQueryClient();
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(50);
  const [filters, setFilters] = useState({
    module: '',
    action: '',
    resourceType: '',
    username: '',
    startDate: '',
    endDate: ''
  });
  const [selectedLog, setSelectedLog] = useState(null);
  const [detailsDialogOpen, setDetailsDialogOpen] = useState(false);
  const [clearLogsDialogOpen, setClearLogsDialogOpen] = useState(false);
  const [clearDays, setClearDays] = useState(90);

  // Fetch activity logs
  const {
    data: logsData,
    isLoading: logsLoading,
    error: logsError,
    refetch: refetchLogs
  } = useQuery(
    ['activityLogs', page, rowsPerPage, filters],
    () => {
      console.log('API Base URL:', api.defaults.baseURL);
      console.log('Making request to:', `/api/activity-logs`);
      return api.get('/api/activity-logs', {
        params: {
          page: page + 1,
          limit: rowsPerPage,
          ...filters
        }
      });
    },
    {
      keepPreviousData: true
    }
  );

  // Fetch statistics
  const {
    data: statsData,
    isLoading: statsLoading
  } = useQuery(
    ['activityStats', filters.startDate, filters.endDate],
    () => {
      console.log('API Base URL for stats:', api.defaults.baseURL);
      console.log('Making stats request to:', `/api/activity-logs/stats`);
      return api.get('/api/activity-logs/stats', {
        params: {
          startDate: filters.startDate,
          endDate: filters.endDate
        }
      });
    }
  );

  // Clear logs mutation
  const clearLogsMutation = useMutation(
    (days) => api.delete('/api/activity-logs', { params: { days } }),
    {
      onSuccess: () => {
        queryClient.invalidateQueries('activityLogs');
        queryClient.invalidateQueries('activityStats');
        setClearLogsDialogOpen(false);
      }
    }
  );

  // Export logs
  const handleExport = () => {
    const params = new URLSearchParams({
      ...filters
    });
    // Use the same base URL as the API instance
    const baseURL = api.defaults.baseURL;
    console.log('Export URL:', `${baseURL}/api/activity-logs/export/csv?${params}`);
    window.open(`${baseURL}/api/activity-logs/export/csv?${params}`, '_blank');
  };

  // Handle filter changes
  const handleFilterChange = (field, value) => {
    setFilters(prev => ({ ...prev, [field]: value }));
    setPage(0);
  };

  // Handle pagination
  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  // Get action color
  const getActionColor = (action) => {
    if (action.includes('CREATED')) return 'success';
    if (action.includes('UPDATED')) return 'info';
    if (action.includes('DELETED')) return 'error';
    if (action.includes('LOGIN')) return 'warning';
    return 'default';
  };

  // Get module icon
  const getModuleIcon = (module) => {
    switch (module) {
      case 'admin': return <Settings />;
      case 'karigar': return <Business />;
      case 'shop': return <Store />;
      case 'transaction': return <AccountBalance />;
      default: return <Settings />;
    }
  };

  // Get user role display
  const getUserRoleDisplay = (role, subadminType) => {
    if (role === 0) return 'Admin';
    return `Subadmin (${subadminType})`;
  };

  // Statistics cards
  const StatCard = ({ title, value, icon, color = 'primary' }) => (
    <Card>
      <CardContent>
        <Box display="flex" alignItems="center" justifyContent="space-between">
          <Box>
            <Typography color="textSecondary" gutterBottom variant="h6">
              {title}
            </Typography>
            <Typography variant="h4" color={color}>
              {value}
            </Typography>
          </Box>
          <Box color={`${color}.main`}>
            {icon}
          </Box>
        </Box>
      </CardContent>
    </Card>
  );

  return (
    <Container maxWidth="xl" sx={{ py: 4 }}>
      {/* Header */}
      <Box textAlign="center" mb={4}>
        <History sx={{ fontSize: 60, color: 'primary.main', mb: 2 }} />
        <Typography variant="h3" component="h1" gutterBottom>
          Activity Log
        </Typography>
        <Typography variant="h5" color="text.secondary" gutterBottom>
          Comprehensive System Activity Monitoring
        </Typography>
      </Box>

      {/* Statistics */}
      {!statsLoading && statsData && (
        <Grid container spacing={3} mb={4}>
          <Grid item xs={12} sm={6} md={3}>
            <StatCard
              title="Total Activities"
              value={statsData.totalActivities}
              icon={<TrendingUp />}
              color="primary"
            />
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <StatCard
              title="Active Users"
              value={statsData.userStats?.length || 0}
              icon={<Person />}
              color="success"
            />
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <StatCard
              title="Most Active Module"
              value={statsData.moduleStats?.[0]?._id || 'N/A'}
              icon={getModuleIcon(statsData.moduleStats?.[0]?._id)}
              color="info"
            />
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <StatCard
              title="Most Common Action"
              value={statsData.actionStats?.[0]?._id || 'N/A'}
              icon={<History />}
              color="warning"
            />
          </Grid>
        </Grid>
      )}

      {/* Filters */}
      <Paper elevation={3} sx={{ p: 3, mb: 3 }}>
        <Box display="flex" alignItems="center" mb={2}>
          <FilterList sx={{ mr: 1 }} />
          <Typography variant="h6">Filters</Typography>
        </Box>
        <Grid container spacing={2}>
          <Grid item xs={12} sm={6} md={2}>
            <FormControl fullWidth size="small">
              <InputLabel>Module</InputLabel>
              <Select
                value={filters.module}
                onChange={(e) => handleFilterChange('module', e.target.value)}
                label="Module"
              >
                <MenuItem value="">All</MenuItem>
                <MenuItem value="admin">Admin</MenuItem>
                <MenuItem value="karigar">Karigar</MenuItem>
                <MenuItem value="shop">Shop</MenuItem>
                <MenuItem value="transaction">Transaction</MenuItem>
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} sm={6} md={2}>
            <FormControl fullWidth size="small">
              <InputLabel>Action</InputLabel>
              <Select
                value={filters.action}
                onChange={(e) => handleFilterChange('action', e.target.value)}
                label="Action"
              >
                <MenuItem value="">All</MenuItem>
                <MenuItem value="USER_LOGIN">User Login</MenuItem>
                <MenuItem value="USER_CREATED">User Created</MenuItem>
                <MenuItem value="USER_UPDATED">User Updated</MenuItem>
                <MenuItem value="USER_DELETED">User Deleted</MenuItem>
                <MenuItem value="KARIGAR_CREATED">Karigar Created</MenuItem>
                <MenuItem value="ORDER_CREATED">Order Created</MenuItem>
                <MenuItem value="TRANSACTION_22K_CREATED">22K Transaction Created</MenuItem>
                <MenuItem value="TRANSACTION_24K_CREATED">24K Transaction Created</MenuItem>
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} sm={6} md={2}>
            <FormControl fullWidth size="small">
              <InputLabel>Resource Type</InputLabel>
              <Select
                value={filters.resourceType}
                onChange={(e) => handleFilterChange('resourceType', e.target.value)}
                label="Resource Type"
              >
                <MenuItem value="">All</MenuItem>
                <MenuItem value="User">User</MenuItem>
                <MenuItem value="Karigar">Karigar</MenuItem>
                <MenuItem value="Order">Order</MenuItem>
                <MenuItem value="Transaction">Transaction</MenuItem>
                <MenuItem value="Customer">Customer</MenuItem>
                <MenuItem value="Item">Item</MenuItem>
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} sm={6} md={2}>
            <TextField
              fullWidth
              size="small"
              label="Username"
              value={filters.username}
              onChange={(e) => handleFilterChange('username', e.target.value)}
            />
          </Grid>
          <Grid item xs={12} sm={6} md={2}>
            <TextField
              fullWidth
              size="small"
              label="Start Date"
              type="date"
              value={filters.startDate}
              onChange={(e) => handleFilterChange('startDate', e.target.value)}
              InputLabelProps={{ shrink: true }}
            />
          </Grid>
          <Grid item xs={12} sm={6} md={2}>
            <TextField
              fullWidth
              size="small"
              label="End Date"
              type="date"
              value={filters.endDate}
              onChange={(e) => handleFilterChange('endDate', e.target.value)}
              InputLabelProps={{ shrink: true }}
            />
          </Grid>
        </Grid>
        <Box display="flex" gap={2} mt={2}>
          <Button
            variant="contained"
            startIcon={<Refresh />}
            onClick={() => refetchLogs()}
          >
            Refresh
          </Button>
          <Button
            variant="outlined"
            startIcon={<Download />}
            onClick={handleExport}
          >
            Export CSV
          </Button>
          <Button
            variant="outlined"
            color="error"
            startIcon={<Delete />}
            onClick={() => setClearLogsDialogOpen(true)}
          >
            Clear Old Logs
          </Button>
        </Box>
      </Paper>

      {/* Activity Logs Table */}
      <Paper elevation={3}>
        <TableContainer>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Timestamp</TableCell>
                <TableCell>User</TableCell>
                <TableCell>Action</TableCell>
                <TableCell>Module</TableCell>
                <TableCell>Resource</TableCell>
                <TableCell>IP Address</TableCell>
                <TableCell>Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {logsLoading ? (
                <TableRow>
                  <TableCell colSpan={7} align="center">
                    <CircularProgress />
                  </TableCell>
                </TableRow>
              ) : logsError ? (
                <TableRow>
                  <TableCell colSpan={7} align="center">
                    <Alert severity="error">Error loading activity logs</Alert>
                  </TableCell>
                </TableRow>
              ) : logsData?.data?.logs?.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={7} align="center">
                    <Typography variant="body1" color="textSecondary">
                      No activity logs found
                    </Typography>
                  </TableCell>
                </TableRow>
              ) : (
                logsData?.data?.logs?.map((log) => (
                  <TableRow key={log.id} hover>
                    <TableCell>
                      <Typography variant="body2">
                        {log.formattedDate}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Box>
                        <Typography variant="body2" fontWeight="bold">
                          {log.username}
                        </Typography>
                        <Typography variant="caption" color="textSecondary">
                          {getUserRoleDisplay(log.userRole, log.userSubadminType)}
                        </Typography>
                      </Box>
                    </TableCell>
                    <TableCell>
                      <Chip
                        label={log.action.replace(/_/g, ' ')}
                        color={getActionColor(log.action)}
                        size="small"
                      />
                    </TableCell>
                    <TableCell>
                      <Box display="flex" alignItems="center">
                        {getModuleIcon(log.module)}
                        <Typography variant="body2" ml={1}>
                          {log.module}
                        </Typography>
                      </Box>
                    </TableCell>
                    <TableCell>
                      <Box>
                        <Typography variant="body2">
                          {log.resourceType}
                        </Typography>
                        {log.resourceName && (
                          <Typography variant="caption" color="textSecondary">
                            {log.resourceName}
                          </Typography>
                        )}
                      </Box>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2" fontFamily="monospace">
                        {log.ipAddress}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Tooltip title="View Details">
                        <IconButton
                          size="small"
                          onClick={() => {
                            setSelectedLog(log);
                            setDetailsDialogOpen(true);
                          }}
                        >
                          <Visibility />
                        </IconButton>
                      </Tooltip>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>
        <TablePagination
          component="div"
          count={logsData?.data?.pagination?.totalItems || 0}
          page={page}
          onPageChange={handleChangePage}
          rowsPerPage={rowsPerPage}
          onRowsPerPageChange={handleChangeRowsPerPage}
          rowsPerPageOptions={[10, 25, 50, 100]}
        />
      </Paper>

      {/* Details Dialog */}
      <Dialog
        open={detailsDialogOpen}
        onClose={() => setDetailsDialogOpen(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>Activity Log Details</DialogTitle>
        <DialogContent>
          {selectedLog && (
            <Box>
              <Grid container spacing={2}>
                <Grid item xs={6}>
                  <Typography variant="subtitle2" color="textSecondary">Timestamp</Typography>
                  <Typography variant="body1">{selectedLog.formattedDate}</Typography>
                </Grid>
                <Grid item xs={6}>
                  <Typography variant="subtitle2" color="textSecondary">Username</Typography>
                  <Typography variant="body1">{selectedLog.username}</Typography>
                </Grid>
                <Grid item xs={6}>
                  <Typography variant="subtitle2" color="textSecondary">User Role</Typography>
                  <Typography variant="body1">
                    {getUserRoleDisplay(selectedLog.userRole, selectedLog.userSubadminType)}
                  </Typography>
                </Grid>
                <Grid item xs={6}>
                  <Typography variant="subtitle2" color="textSecondary">Action</Typography>
                  <Chip
                    label={selectedLog.action.replace(/_/g, ' ')}
                    color={getActionColor(selectedLog.action)}
                    size="small"
                  />
                </Grid>
                <Grid item xs={6}>
                  <Typography variant="subtitle2" color="textSecondary">Module</Typography>
                  <Typography variant="body1">{selectedLog.module}</Typography>
                </Grid>
                <Grid item xs={6}>
                  <Typography variant="subtitle2" color="textSecondary">Resource Type</Typography>
                  <Typography variant="body1">{selectedLog.resourceType}</Typography>
                </Grid>
                <Grid item xs={6}>
                  <Typography variant="subtitle2" color="textSecondary">Resource Name</Typography>
                  <Typography variant="body1">{selectedLog.resourceName || 'N/A'}</Typography>
                </Grid>
                <Grid item xs={6}>
                  <Typography variant="subtitle2" color="textSecondary">IP Address</Typography>
                  <Typography variant="body1" fontFamily="monospace">{selectedLog.ipAddress}</Typography>
                </Grid>
                <Grid item xs={12}>
                  <Typography variant="subtitle2" color="textSecondary">User Agent</Typography>
                  <Typography variant="body2" fontFamily="monospace" sx={{ wordBreak: 'break-all' }}>
                    {selectedLog.userAgent}
                  </Typography>
                </Grid>
                {selectedLog.details && (
                  <Grid item xs={12}>
                    <Typography variant="subtitle2" color="textSecondary">Details</Typography>
                    <Paper variant="outlined" sx={{ p: 2, mt: 1 }}>
                      <pre style={{ margin: 0, fontSize: '0.875rem' }}>
                        {JSON.stringify(selectedLog.details, null, 2)}
                      </pre>
                    </Paper>
                  </Grid>
                )}
              </Grid>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDetailsDialogOpen(false)}>Close</Button>
        </DialogActions>
      </Dialog>

      {/* Clear Logs Dialog */}
      <Dialog
        open={clearLogsDialogOpen}
        onClose={() => setClearLogsDialogOpen(false)}
      >
        <DialogTitle>Clear Old Activity Logs</DialogTitle>
        <DialogContent>
          <Typography variant="body1" gutterBottom>
            This will permanently delete activity logs older than the specified number of days.
          </Typography>
          <TextField
            fullWidth
            type="number"
            label="Days to keep"
            value={clearDays}
            onChange={(e) => setClearDays(e.target.value)}
            sx={{ mt: 2 }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setClearLogsDialogOpen(false)}>Cancel</Button>
          <Button
            onClick={() => clearLogsMutation.mutate(clearDays)}
            color="error"
            variant="contained"
            disabled={clearLogsMutation.isLoading}
          >
            {clearLogsMutation.isLoading ? <CircularProgress size={20} /> : 'Clear Logs'}
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
};

export default ActivityLog; 